import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { Button, Menu, MenuItem } from '@material-ui/core';
import { ShareModalContext } from 'common/context';
import { submitPassportRequest } from '../../actions/submitPassportRequest';

// Dedicated CANDIDATE self-view passport menu ("Your Passports"). Deliberately separate from the
// sponsor-facing common/components/PassportActionMenu (left untouched) so it can't regress.
//
// Access model mirrors Passport.jsx `isPassportAnon`:
//   hasFullAccess = self-sponsored OR the sponsor granted the candidate's request (feedback === 1).
// Options:
//   - Anonymous Results — always (own results, anonymised)
//   - View full passport — only when the candidate actually has full access (else it would render anon)
//   - Request full passport — only when they don't (candidate flow: submitPassportRequest feedback:0)
//   - Share — always (opens ShareModal; passes the request flow when access is not yet granted)
const CandidatePassportMenu = (props) => {
  const { candidate, anchorEl, onClose, userInfo } = props;
  const { handleSetSelectCandidate, handleShowShareModal } =
    useContext(ShareModalContext);

  if (!candidate) return null;

  const { flags } = candidate;
  const feedback = candidate.passportRequest
    ? candidate.passportRequest.feedback
    : undefined;
  const hasFullAccess = (flags && flags.selfSponsored) || feedback === 1;
  const pending = feedback === 0;

  const handleRequest = () => {
    props.submitPassportRequest(
      { userId: userInfo.id, candidateId: candidate.id, feedback: 0 },
      onClose,
    );
  };

  const handleShare = () => {
    handleSetSelectCandidate(candidate);
    handleShowShareModal(
      hasFullAccess ? undefined : { selfRequestHandler: handleRequest },
    );
    onClose();
  };

  return (
    <Menu
      id="candidate-passport-action-menu"
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
    >
      <MenuItem>
        <Button
          component={Link}
          variant="contained"
          color="primary"
          to={`/passport-anon/${candidate.id}/1`}
          target="_blank"
          rel="noopener noreferrer"
          fullWidth
        >
          Anonymous Results
        </Button>
      </MenuItem>

      {hasFullAccess && (
        <MenuItem>
          <Button
            component={Link}
            variant="contained"
            color="primary"
            to={`/passport/${candidate.id}`}
            target="_blank"
            rel="noopener noreferrer"
            fullWidth
          >
            View Passport
          </Button>
        </MenuItem>
      )}

      {!hasFullAccess && pending && (
        <MenuItem>
          <Button variant="contained" fullWidth disabled>
            Requested, awaiting sponsor
          </Button>
        </MenuItem>
      )}

      {!hasFullAccess && !pending && (
        <MenuItem>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRequest}
            fullWidth
          >
            Request full passport
          </Button>
        </MenuItem>
      )}

      <MenuItem>
        <Button
          variant="contained"
          color="primary"
          onClick={handleShare}
          fullWidth
        >
          Share
        </Button>
      </MenuItem>
    </Menu>
  );
};

CandidatePassportMenu.propTypes = {
  candidate: PropTypes.object,
  anchorEl: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  userInfo: PropTypes.object.isRequired,
  submitPassportRequest: PropTypes.func.isRequired,
};

CandidatePassportMenu.defaultProps = {
  candidate: null,
  anchorEl: null,
};

const mapStateToProps = (state) => {
  const { userInfo } = state.dashboard;
  return { userInfo };
};

export default connect(mapStateToProps, { submitPassportRequest })(
  CandidatePassportMenu,
);
